/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import { Title } from "../../component/TitleUtils";
import { PasswordField } from "../../component/FormUtils";
import { MyActionButton } from "../../component/ActionButtonUtils";
import { getOneAdmin } from "../../../../middlewares/sysNavigation/SysNavigation";
import { validatePassword } from "../../../../controllers/admin/adminControllers";
import { useAdminsContext } from "../../../../context/admin/context/AdminManageContext";

const PasswordSettings = ({
  loading,
  setError,
  password,
  setAction,
  setSuccess,
  setPassword,
  setErrorMessage,
  confirm_password,
  setConfirmPassword,
  setLoading,
  responses,
}) => {
  const { updatePassword } = useAdminsContext();
  const admin = getOneAdmin();
  const admin_id = admin?.admin_id;

  const updatePasswordHandler = async (e) => {
    e.preventDefault();
    setLoading(true);
    const { isError, message } = validatePassword(password, confirm_password);
    if (isError) {
      responses(true, message);
    } else if (password !== confirm_password) {
      responses(true, "Passwords Should Match");
    } else {
      await sendPasswordRequest();
    }
  };

  const sendPasswordRequest = async () => {
    const data = { password: password };
    const { message, status } = await updatePassword(admin_id, data);
    if (status === 200 || status === 201) {
      setPassword(null);
      setConfirmPassword(null);
      responses(false, message);
    } else {
      responses(true, message);
    }
  };

  // Visual-only strength + requirement checks
  const meets = {
    length: (password || "").length >= 8,
    upper: /[A-Z]/.test(password || ""),
    number: /\d/.test(password || ""),
    symbol: /[^A-Za-z0-9]/.test(password || ""),
  };
  const metCount = Object.values(meets).filter(Boolean).length;

  const strength = (() => {
    if (!password) return { label: "Start typing…", tone: "slate", width: "w-0" };
    if (metCount <= 1) return { label: "Very weak", tone: "rose", width: "w-1/4" };
    if (metCount === 2) return { label: "Weak", tone: "amber", width: "w-1/2" };
    if (metCount === 3) return { label: "Good", tone: "sky", width: "w-3/4" };
    return { label: "Strong", tone: "emerald", width: "w-full" };
  })();

  const confirmMatch =
    (password || "").length > 0 &&
    (confirm_password || "").length > 0 &&
    password === confirm_password;

  const toneBar = {
    slate: "from-slate-300 to-slate-400",
    rose: "from-rose-500 to-rose-400",
    amber: "from-amber-500 to-amber-400",
    sky: "from-sky-500 to-sky-400",
    emerald: "from-emerald-600 to-emerald-500",
  }[strength.tone];

  return (
    <form onSubmit={updatePasswordHandler} className="mt-6">
      <div className="mx-auto w-full max-w-4xl overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-amber-500 to-fuchsia-500" />

        {/* Header - centered and minimal */}
        <div className="px-6 pt-5 text-center">
          <Title props={{ title: "2. Update Password" }} />
          <p className="mx-auto mt-1 max-w-xl text-xs text-gray-600">
            Use a unique passphrase. You’ll see strength and requirements update as you type.
          </p>
        </div>

        {/* Fields */}
        <div className="px-6 pt-4 pb-1">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <PasswordField
              value={password}
              required={true}
              onChange={(e) => {
                setPassword(e.target.value);
                setError(null);
                setSuccess(null);
              }}
            />
            <PasswordField
              value={confirm_password}
              isConfirmPassword={true}
              required={true}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setError(null);
                setSuccess(null);
              }}
            />
          </div>
        </div>

        <div className="px-6">
          <div className="mx-auto mt-3 max-w-xl">
            <div className="flex items-center justify-between text-[11px]">
              <span className="font-semibold text-gray-700">Strength</span>
              <span
                className={[
                  "font-semibold",
                  strength.tone === "emerald"
                    ? "text-emerald-700"
                    : strength.tone === "sky"
                      ? "text-sky-700"
                      : strength.tone === "amber"
                        ? "text-amber-700"
                        : strength.tone === "rose"
                          ? "text-rose-700"
                          : "text-slate-600",
                ].join(" ")}
                aria-live="polite"
              >
                {strength.label}
              </span>
            </div>
            <div className="mt-1 h-2 w-full rounded-full bg-gray-100 ring-1 ring-gray-200">
              <div
                className={[
                  "h-2 rounded-full bg-gradient-to-r transition-all duration-300",
                  strength.width,
                  toneBar,
                ].join(" ")}
              />
            </div>
          </div>
        </div>

        <div className="px-6">
          <div className="mx-auto mt-3 max-w-3xl">
            <div className="flex flex-wrap items-center justify-center gap-2">
              <RequirementPill ok={meets.length} label="8+ characters" />
              <RequirementPill ok={meets.upper} label="Uppercase" />
              <RequirementPill ok={meets.number} label="Number" />
              <RequirementPill ok={meets.symbol} label="Symbol" />
              <MatchPill ok={confirmMatch} hasInput={!!(password || confirm_password)} />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-5 border-t border-gray-100 bg-white px-6 py-4">
          <div className="flex items-center justify-end">
            <MyActionButton
              loading={loading}
              title="Update Password"
              onClick={() => {
                setError(false);
                setSuccess(false);
                setAction("password");
                setErrorMessage(null);
              }}
            />
          </div>
        </div>
      </div>
    </form>
  );
};

export default PasswordSettings;

const RequirementPill = ({ ok, label }) => {
  return (
    <span
      className={[
        "inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-[11px] font-semibold ring-1",
        ok
          ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
          : "bg-gray-50 text-gray-600 ring-gray-200",
      ].join(" ")}
      aria-checked={ok}
      role="checkbox"
    >
      <Dot ok={ok} />
      {label}
    </span>
  );
};

const MatchPill = ({ ok, hasInput }) => {
  const label = ok ? "Passwords match" : hasInput ? "Awaiting match" : "Start typing…";
  const cls = ok
    ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
    : hasInput
      ? "bg-amber-50 text-amber-800 ring-amber-200"
      : "bg-slate-100 text-slate-700 ring-slate-200";
  return (
    <span
      className={[
        "inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-[11px] font-semibold ring-1",
        cls,
      ].join(" ")}
      aria-live="polite"
    >
      <Dot ok={ok} warn={!ok && hasInput} idle={!ok && !hasInput} />
      {label}
    </span>
  );
};

const Dot = ({ ok, warn, idle }) => {
  const tone = ok ? "bg-emerald-600" : warn ? "bg-amber-500" : idle ? "bg-slate-400" : "bg-gray-300";
  return <span className={["inline-block h-1.5 w-1.5 rounded-full", tone].join(" ")} aria-hidden="true" />;
};
